import { Base } from '../../abstract/base.model';
import { GeoJsonPolygon } from './geo-json-polygon.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';

/**
 * A field (farm location) of the organization, built from the hub's field JSON (`data-models/field.ts`).
 * Fields belong to customers and are the target of spray operations. The list endpoint adds `hasBoundary`.
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays `null`),
 * so a form built from a field can't blank a value. Display fallbacks belong in templates.
 */
export class Field extends Base {
  // Writable fields
  customerId?: string | null;
  locationId?: string | null;
  name?: string | null;
  acreage?: number | null;
  cropType?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  emergencyContactName?: string | null;
  emergencyContactNumber?: string | null;
  loadMixLocationLatitude?: number | null;
  loadMixLocationLongitude?: number | null;
  loadMixLocationDescription?: string | null;
  boundaryKml?: string | null;
  /** Only a GeoJSON Polygon is read; any other shape stays `undefined` (and so is never sent back). */
  boundaryGeojson?: GeoJsonPolygon | null;
  notes?: string | null;

  // Read-only
  organizationId?: string | null;
  agremoFieldId?: string | null;
  /** Sent by the list and options endpoints instead of the boundary itself. */
  hasBoundary?: boolean | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.customerId = nullableString(source['customerId']);
    this.locationId = nullableString(source['locationId']);
    this.name = nullableString(source['name']);
    this.acreage = nullableNumber(source['acreage']);
    this.cropType = nullableString(source['cropType']);
    this.latitude = nullableNumber(source['latitude']);
    this.longitude = nullableNumber(source['longitude']);
    this.address = nullableString(source['address']);
    this.city = nullableString(source['city']);
    this.state = nullableString(source['state']);
    this.emergencyContactName = nullableString(source['emergencyContactName']);
    this.emergencyContactNumber = nullableString(source['emergencyContactNumber']);
    this.loadMixLocationLatitude = nullableNumber(source['loadMixLocationLatitude']);
    this.loadMixLocationLongitude = nullableNumber(source['loadMixLocationLongitude']);
    this.loadMixLocationDescription = nullableString(source['loadMixLocationDescription']);
    this.boundaryKml = nullableString(source['boundaryKml']);
    this.boundaryGeojson = geoJsonPolygon(source['boundaryGeojson']);
    this.notes = nullableString(source['notes']);
    this.organizationId = nullableString(source['organizationId']);
    this.agremoFieldId = nullableString(source['agremoFieldId']);
    this.hasBoundary = nullableBoolean(source['hasBoundary']);
  }
}

function geoJsonPolygon(value: unknown): GeoJsonPolygon | null | undefined {
  if (value === null) {
    return null;
  }
  const isPolygon = isRecord(value) && value['type'] === 'Polygon' && Array.isArray(value['coordinates']);
  return isPolygon ? { type: 'Polygon', coordinates: value['coordinates'] as number[][][] } : undefined;
}
